import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { config } from '@app/core/app-config';
import { Filter } from '@app/shared/models/filter';
import { Segment } from '@app/shared/models/segment';
import { environment } from '@env/environment';
import { Logger } from '@utils';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { ApiCacheService } from '../api-cache.service';
import { PusherService } from '../pusher/pusher.service';

@Injectable({
  providedIn: 'root',
})
export class SegmentService implements OnDestroy {
  private getSegmentListUrl = `${config.API_URL}/segments/`;
  private destroy$ = new Subject<void>();
  private segmentUpdateSubscription: Subscription;

  segmentsSbj = new BehaviorSubject([]);

  constructor(
    private http: HttpClient,
    private apiCache: ApiCacheService,
    private pusherService: PusherService
  ) {
    this.listenForSegmentUpdates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.segmentUpdateSubscription) {
      this.segmentUpdateSubscription.unsubscribe();
    }
  }

  private listenForSegmentUpdates(): void {
    this.segmentUpdateSubscription = this.pusherService.segmentUpdate$
      .pipe(takeUntil(this.destroy$))
      .subscribe((updatedSegment) => {
        if (updatedSegment) {
          this.updateSingleSegment(updatedSegment);
          Logger.log('Segment updated via Pusher:', updatedSegment.uuid, 'badge_count:', updatedSegment.badge_count);
        }
      });
  }

  private updateSingleSegment(updatedSegment: Segment): void {
    const currentSegments = this.segmentsSbj.value;
    const updatedSegments = currentSegments.map((segment) =>
      segment.uuid === updatedSegment.uuid ? updatedSegment : segment
    );
    this.segmentsSbj.next(updatedSegments);
  }

  getSegmentListForKey(key: string) {
    const url = `${this.getSegmentListUrl}${key}`;
    return this.http.get<Segment[]>(url).pipe(
      map((response) => {
        this.segmentsSbj.next(response['data']);
      })
    );
  }

  getSegmentById(id: string, key?: string): Observable<any> {
    let url;

    if (id === 'default' && key) {
      url = `${this.getSegmentListUrl}default/${key}`;
    } else {
      url = `${this.getSegmentListUrl}${id}`;
    }

    return this.http.get(url).pipe(
      map((response) => {
        return {
          filters: response['data']['filter_body'],
          title: response['data']['label'],
          uuid: response['data']['uuid'],
          identifier: response['data']['identifier'],
        };
      })
    );
  }

  deleteSegment(id: string, key: string): Observable<any> {
    const url = `${this.getSegmentListUrl}${id}`;

    return this.http.delete(url).pipe(
      map((response) => {
        return response;
      })
    );
  }

  saveCustomSegment(key: string, name: string, filters: Filter[]): Observable<any> {
    const url = `${this.getSegmentListUrl}`;

    const postData = {
      group: 'Saved Segments',
      type: key,
      name,
      filters,
    };

    this.apiCache.delete(`${environment.apiUrl}/segments/${key}`);

    return this.http.post<any>(url, postData).pipe(
      map((response) => response['data']),
      tap((saveSuccess) => {
        if (saveSuccess) {
          this.getSegmentListForKey(key).subscribe();
        }
      })
    );
  }
}
